import React, { useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import InlineSpinner from './InlineSpinner';
import ScannerEntryModeToggle from './ScannerEntryModeToggle';
import type { ScannerEntryMode } from './types/ScannerTypes';

type Props = {
  value: string;
  connected: boolean;
  disabled?: boolean;
  loading?: boolean;
  pulse?: boolean;
  autoFocus?: boolean;
  inputRef?: React.RefObject<HTMLInputElement | null>;
  onChange: (value: string) => void;
  onSubmit: (value: string) => void;
  /** Extra classes applied to the outer wrapper div (only when the toggle is shown). */
  wrapperClassName?: string;
  className?: string;
  /**
   * When provided, renders a small Scan/Manual pill above the input. In manual
   * mode auto-submit-on-scan is suppressed so users can freely type a value
   * and confirm with Enter / a sibling button.
   */
  mode?: ScannerEntryMode;
  onModeChange?: (mode: ScannerEntryMode) => void;
  /**
   * Optional transform applied before values reach `onChange` / `onSubmit`.
   * Use to convert Raceranger QR URLs into fleet device IDs.
   */
  parseValue?: (raw: string) => string;
  placeholderScan?: string;
  placeholderManual?: string;
  placeholderDisconnected?: string;
  onFocus?: () => void;
};

export default function BarcodeScannerEntryField(props: Props): React.ReactElement {
  const {
    value,
    connected,
    disabled = false,
    loading = false,
    pulse = false,
    autoFocus = false,
    inputRef,
    onChange,
    onSubmit,
    className = 'w-full h-9 rounded-lg border border-slate-300 bg-white/95 px-3 text-sm text-slate-900 placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-200 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100 dark:placeholder:text-slate-400',
    wrapperClassName,
    mode,
    onModeChange,
    parseValue,
    placeholderScan,
    placeholderManual,
    placeholderDisconnected,
    onFocus,
  } = props;

  const lastAutoSubmittedRef = useRef('');
  const autoSubmitEnabled = connected && mode !== 'manual';

  const transform = (raw: string): string => (parseValue ? parseValue(raw) : raw);
  const handleChange = (raw: string): void => onChange(transform(raw));
  const handleSubmit = (raw: string): void => onSubmit(transform(raw));
  const handleClear = (): void => {
    lastAutoSubmittedRef.current = '';
    onChange('');
    inputRef?.current?.focus();
  };
  const showClear = !disabled && !loading && value.length > 0;

  useEffect(() => {
    if (!autoSubmitEnabled) return;
    const trimmed = String(value || '').trim();
    if (!trimmed || disabled) return;
    if (trimmed === lastAutoSubmittedRef.current) return;

    const timer = window.setTimeout(() => {
      lastAutoSubmittedRef.current = trimmed;
      handleSubmit(trimmed);
    }, 120);

    return () => window.clearTimeout(timer);
  }, [autoSubmitEnabled, disabled, value]);

  useEffect(() => {
    if (!value.trim()) {
      lastAutoSubmittedRef.current = '';
    }
  }, [value]);

  const placeholder =
    mode === 'manual'
      ? (placeholderManual ?? 'Type a value')
      : connected
        ? (placeholderScan ?? 'Scan barcode')
        : (placeholderDisconnected ?? 'Focus to connect scanner');

  const hasToggle = !!mode && !!onModeChange;

  if (hasToggle) {
    return (
      <div
        className={[
          'flex w-full min-h-9 min-w-0 items-stretch overflow-hidden rounded-lg border border-gray-300 bg-white shadow-sm transition-colors duration-100',
          'focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-500/20',
          'dark:border-slate-600 dark:bg-slate-900 dark:focus-within:border-blue-500 dark:focus-within:ring-blue-500/25',
          pulse
            ? 'animate-pulse border-blue-400 ring-2 ring-blue-400/60 dark:border-blue-400 dark:ring-blue-500/50'
            : '',
          wrapperClassName ?? '',
        ].filter(Boolean).join(' ')}
      >
        <input
          ref={inputRef}
          autoFocus={autoFocus}
          type="text"
          value={value}
          readOnly={mode !== 'manual'}
          onChange={(e) => { if (mode === 'manual') handleChange(e.target.value); }}
          onFocus={onFocus}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              handleSubmit(value);
            }
          }}
          placeholder={placeholder}
          className={[
            className,
            'h-9 min-w-0 flex-1 border-0 bg-transparent shadow-none outline-none ring-0',
            'rounded-none focus:border-transparent focus:ring-0 focus:outline-none',
            'dark:h-9 dark:bg-transparent',
            pulse ? 'bg-blue-50/80 dark:bg-blue-950/50' : '',
            mode !== 'manual' ? 'caret-transparent' : '',
          ]
            .filter(Boolean)
            .join(' ')}
          disabled={disabled}
        />
        <div className="ml-auto flex h-9 shrink-0 items-center gap-2 border-l border-gray-200 bg-slate-50 px-2.5 dark:border-slate-700 dark:bg-slate-800/80">
          {loading ? (
            <span className="text-gray-500 dark:text-slate-400" aria-label="Submitting recall">
              <InlineSpinner />
            </span>
          ) : null}
          {showClear ? (
            <button
              type="button"
              onClick={handleClear}
              className="rounded-full p-0.5 text-gray-400 hover:bg-gray-200 hover:text-gray-700 dark:text-slate-500 dark:hover:bg-slate-700 dark:hover:text-slate-200"
              title="Clear"
              aria-label="Clear value"
            >
              <X className="h-3.5 w-3.5" strokeWidth={2.5} />
            </button>
          ) : null}
          <ScannerEntryModeToggle
            mode={mode}
            onChange={onModeChange}
            connected={connected}
            disabled={disabled}
            embedded
          />
        </div>
      </div>
    );
  }

  return (
    <div className={['relative', wrapperClassName ?? ''].filter(Boolean).join(' ')}>
      <input
        ref={inputRef}
        autoFocus={autoFocus}
        type="text"
        value={value}
        readOnly={mode !== 'manual'}
        onChange={(e) => { if (mode === 'manual') handleChange(e.target.value); }}
        onFocus={onFocus}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            handleSubmit(value);
          }
        }}
        placeholder={placeholder}
        className={[
          className,
          (loading || showClear) ? 'pr-9' : '',
          pulse
            ? 'animate-pulse border-blue-400 ring-2 ring-blue-400/60 bg-blue-50/80 dark:border-blue-400 dark:bg-blue-950/50'
            : '',
          mode !== 'manual' ? 'caret-transparent' : '',
        ].filter(Boolean).join(' ')}
        disabled={disabled}
      />
      {loading ? (
        <span
          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 dark:text-slate-400"
          aria-label="Submitting recall"
        >
          <InlineSpinner />
        </span>
      ) : showClear ? (
        <button
          type="button"
          onClick={handleClear}
          className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full p-0.5 text-gray-400 hover:bg-gray-200 hover:text-gray-700 dark:text-slate-500 dark:hover:bg-slate-700 dark:hover:text-slate-200"
          title="Clear"
          aria-label="Clear value"
        >
          <X className="h-3.5 w-3.5" strokeWidth={2.5} />
        </button>
      ) : null}
    </div>
  );
}
